import { ErrorHandler } from "UI/Errors";
import { IAwaitableEvent } from "UI/UI.BaseControls/AwaitableEvent";
import { ListDataGrid } from "UI/UI.Controls/ListDataGrid";
import { ControlRightTypesGroup } from "UI/UI/Rights/RightGroups";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { IOrderBy } from "../../../../../Global/Base/DataSources/Interfaces";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { Deferred, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { ActionType } from "../../../../UI.Controls/Actions/IAction";
import { Column } from "../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory, IColumnDefinition } from "../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { TreeViewDataRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/TreeViewDataRow";
import { EditableDBDataSource, IActualObjectChangeEventArgs, SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { IObjectDataSource } from "../../../../UI.Controls/DataSource/IObjectDataSource";
import { TreeViewDataSource } from "../../../../UI.Controls/DataSource/TreeViewDataSource";
import { IDataGridSelectionChangedEvent } from "../../../../UI.Controls/IEvent";
import { TreeViewGrid } from "../../../../UI.Controls/TreeViewGrid";
import { Control } from "../../Controls/Control";
import { FormControlWithLoggerParams } from "../../Controls/ControlInterfaces";
import { ControlUtils } from "../../Controls/ControlUtils";
import { UIManager } from "../../UIManager";
import { IActualObjectChangedEventArgs } from "../Frames";
import { BaseFrame } from "./BaseFrame";
import { FrameSharedMethods } from "./FrameSharedMethods";
import { ITreeViewFrame } from "./ITreeViewFrame";

export class TreeViewFrame extends BaseFrame implements ITreeViewFrame {
    /**
     * Zakáže možnost řazení
     */
    public disableSort: boolean = false;

    private _dataGrid: TreeViewGrid;
    private _dataSource: IObjectDataSource; //IDataSource;
    private _hierarchyAttr: string = "Hierarchie";
    private _dataLoadingDefferred: Deferred<boolean> = PromiseUtils.defer();
    // Pomocne promenne pro nenacitani hodnot a prav skrytych elementu
    private _cvlMasterChangeRequest: boolean = false;
    private _canValueLoadChangedListener: (control: Control, value: boolean) => Promise<void>;

    private _orderBy: IOrderBy[];

    public get orderBy(): IOrderBy[] {
        return this._orderBy;
    }

    public set orderBy(v: IOrderBy[]) {
        this._orderBy = v;
        if (this.dataGrid)
            this.dataGrid.orderBy = v;
    }

    public get isTreeViewFrame(): boolean {
        return true;
    }

    public get dataGrid(): TreeViewGrid {
        return this._dataGrid;
    }

    public get dataSource(): IObjectDataSource {
        return this._dataSource;
    }

    public set dataSource(v: IObjectDataSource) {
        this._dataSource = v;
    }

    public override get visible(): boolean {
        return super.visible;
    }

    public override set visible(value: boolean) {
        const oldVisible = super.visible;
        super.visible = value;
        if (value && value !== oldVisible && this._cvlMasterChangeRequest) {
            this._onMasterObjectChanged();
        }
    }

    /**
    * Zda je frame aktivni a lze s nim pracovat a poustet nad nim akce (musi byt povoleny pravy a nesmi byt ve stavu jen ke cteni)
    */
    public override get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    constructor(params: FormControlWithLoggerParams) {
        super(params);

        this._dataLoadingDefferred.resolve(false);

        this.element.classList.add("tree-view-frame", "dataGrid");

        this.condition = this.element.getAttribute("data-condition");
        const condParamsString = this.element.getAttribute("data-conditionparamsstring");
        if (condParamsString)
            this.conditionParamsAttrs = condParamsString.split(";");

        const hieararchyAttribute = this.element.getAttribute("data-hierarchyAttr");
        if (hieararchyAttribute)
            this._hierarchyAttr = hieararchyAttribute;

        this.disableSort = DataConverter.toBoolean(this.element.getAttribute("data-DisableSort"), false);

        const ordering = this.element.getAttribute("data-orderby");
        this.orderBy = FrameSharedMethods.parseOrderBy(ordering, ListDataGrid.getDefaultOrderByDefinition(this.dataSource));
    }

    public override waitForDataLoad(): Promise<boolean> {
        return this._dataLoadingDefferred.promise;
    }

    protected override async _initializeFormUI(): Promise<void> {
        this.menu = this.ownerForm.formUI.createFrameMenu(this);

        if (!this.menu)
            throw new InvalidOperationException("Unable to create frame menu");

        //if (!this.isMainFrame)
        //this.headerColor = Pages.getColor();

        /*const factory = (dataKeyFetcher: () => string) => this.menu.renderRecordControls(this.dataGrid, dataKeyFetcher);
        const registerDGFactoryFnc = () => {
            if (this.dataGrid)
                this.dataGrid.registerRowControlsFactory(factory);
        };*/
        await this.actualizeRights(); // není objekt, na kterém by se vyhodnocovalo -> pouze vytvoří instanci s daty s defaultními právy

        // toolbar musí být vidět, pokud není nastaveno, aby byl skrytý a zároveň je vidět seznam nebo seznam má detail, který je vidět
        this.menu.setVisibility(!this.hideToolBar && (this.canValueLoad || (this.detail != null && this.detail.canValueLoad)));
    }

    protected override async _createColumns(columnDefinitions: IColumnDefinition[]): Promise<any> {
        const columns = new Array<Column>();
        let attrs = new Array<string>();

        try {
            const attrMap = await FrameSharedMethods.mapColumnDefinitionsToAttributeInfos(this.objectName, columnDefinitions);

            for (const attr of attrMap) {
                const col: Column = await ColumnFactory.initColumn(attr.columnDefinition, attr.attributeInfo.type, attr.attributeInfo, undefined, this.disableSort);

                col.columnGrow = true;

                columns.push(col);
                this._dataGrid.appendChild(col);

                if (col.visible)
                    attrs = attrs.concat(col.dataSourceAttrNames);
            }

            this.dataGrid.columns = columns;
            const fullLengthAttributes = columns.filter(column => column.trimValue === false).map(column => column.attrName);

            this.dataSource.setAttrs(attrs, fullLengthAttributes);
        } catch (err) {
            this._logger.error(`Unable to load column attribute info for ${JSON.stringify(columnDefinitions.map(column => column.attrName))}`); //`
            throw err;
        }
    }

    /**
    * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
    */
    protected override async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */

        try {
            UIManager.instance.showLoader();

            // obecná inicializace frame
            await this._initializeFrame();
            // navázání listenerů
            await this._initializeDataSource();
            // vytvoření a nastavení DataGrid
            await this._initializeDataGrid();
            // vytvoření menu
            await this._initializeFormUI();
            // zpracování a zaregistrování akcí do menu
            await this._initializeActions();
            // inicializace práv
            await this._initializeRights();

            this._isInitialized = true;
            this.initialized.trigger(this, undefined);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    protected override async _initializeActions(): Promise<void> {
        await super._initializeActions();

        // Při skrytém toolbaru nebudeme přidávat akce
        if (!this.hideToolBar) {
            this._addStandartActions(this.isMainFrame);

            this.addCustomActions();
        }

        await this._newEvaluateRightsGroup(ControlRightTypesGroup.attributeChange);
    }

    protected _addStandartActions(isMainFrame: boolean): void {
        if (!this.hideButtons.refresh && !(isMainFrame && this.ownerForm.visibleOnlyDetail)) {
            const refreshAction = FrameSharedMethods.createStandardAction(ActionType.standardRefreshList, () => this.refreshOnClick(), this);
            refreshAction.enabled = true;
            this.menu.addAction(refreshAction);
        }
    }

    protected async _initializeDataGrid(): Promise<void> {
        const initDataGridFnc = async (): Promise<void> => {
            this._dataGrid = new TreeViewGrid();
            this._dataGrid.objectName = this.objectName;
            this._dataGrid.orderBy = this.orderBy;
            this._dataGrid.setExtendedProperties(this._getExtendedProperties());

            if (this._dataGrid.emptyMessage) {
                this._dataGrid.emptyMessage.emptyMessageCustomization = this.emptyMessageCustomization;
            }

            if (this.disableSort)
                this._dataGrid.disableSort();

            this._dataGrid.dataSource = (this.dataSource as TreeViewDataSource);
            await this._createColumns(FrameSharedMethods.getColumnsDefinition(this.element));

            this._dataGrid.headerRow.setCaption(this.locKey, this._showCaption);

            this.dataGrid.addEventListener("SelectionChanged", (e: IDataGridSelectionChangedEvent) => {
                this.onSelectionChanged(e);
            });

            this.dataGrid.registerEventListener("SelectionChangedPreview", (event: IAwaitableEvent<boolean>) => {
                // K možným existujícím taskům z jiných handlerů přidám další - výsledkem celého chainu může být zrušení cílové události (při false)
                event.enqueueResult(async (result) => {
                    // Pokud už někde dříve bylo výsledkem false, tak nemá cenu provádět další vyhodnocení
                    if (result === false) {
                        return false;
                    }

                    // Počkám na možné probíhající updaty a reloady
                    await this.dataSource.waitForReload();

                    return this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext });
                });
            });

            this.container.appendChild(this.dataGrid);

            if (this.masterFrame) {
                //this.masterFrame.element.addEventListener("ActualObjectChangedEvent", () => {
                this.masterFrame.actualObjectChanged.add(async (_sender, eventArgs) => {
                    this._cvlMasterChangeRequest = true;
                    if (this.masterFrame)
                        await this.masterFrame.rightsEvaluationTask;
                    this._onMasterObjectChanged(eventArgs);
                });
            } else {
                if (this.ownerForm.queryMode) {
                    this.dataGrid.setToQueryMode(() => this.refreshOnClick());
                } else {
                    this.dataGrid.loadData();
                }
            }

            this.dataGrid.dataSource.loadingStarted.add(() => {
                UIManager.instance.showLoader();
                this._dataLoadingDefferred = PromiseUtils.defer();
            });

            this.dataGrid.dataSource.dataLoaded.add(() => {
                this._dataLoadingDefferred.resolve(true);
                this.dataLoaded.trigger(this, null);
            });

            this.dataGrid.dataSource.loadingEnded.add(() => {
                UIManager.instance.hideLoader();
            });

            this.dataGrid.dataSource.error.add(() => {
                UIManager.instance.hideLoader();
                this._dataLoadingDefferred.reject();
                this.dataLoadingFailed.trigger(this, null);
            });

            this.dataGrid.dataSource.loadingCancelled.add(() => {
                UIManager.instance.hideLoader();
                this._dataLoadingDefferred.resolve(false);
                this.dataLoadingCancelled.trigger(this, null);
            });

            // pokud už má master frame načtený objekt, tak se o tom vyvolá oznámení
            if (this.masterFrame && this.masterFrame.actualObject != null) {
                this._cvlMasterChangeRequest = true;
                this._onMasterObjectChanged();
            }
        };

        if (this.canValueLoad) {
            await initDataGridFnc();
        } else {
            this.canValueLoadChanged.add(async () => {
                await initDataGridFnc();
                if (this.masterObject)
                    this._onMasterObjectChanged();

                // Skryvani a objevovani zalozky pri vstupu na frame
                if (!this.hideToolBar) //pokud mam schovat toolbar
                    this.menu.setVisibility(!this.hideToolBar && this.visible);
            }, true);
        }

        //let delayedMasterObjectChangeTimeoutID: number = undefined;
        this.canValueLoadChanged.add(this._canValueLoadChangedListener = async (_control: Control, _value: boolean) => {
            // if (value) {
            //     if (!this.dataGrid) {
            //         await f_initDG();
            //     }
            //     if (delayedMasterObjectChangeTimeoutID)
            //         clearTimeout(delayedMasterObjectChangeTimeoutID);
            //     delayedMasterObjectChangeTimeoutID = self.setTimeout(async () => {
            //         if (this._cvlMasterChangeRequest)
            //             this._onMasterObjectChanged();
            //         await this._setRightsOnStandartAction();
            //         await this.checkRights();
            //     }, 200);
            // }
            //TODO-rites: end remove
            if (this._cvlMasterChangeRequest)
                await this._onMasterObjectChanged();
            await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.initialized); // TODO-rites: ~nechat tady? asi jo; c2: spouštět až po inicializaci všech framů, jinak mohlo dojít k situaci, že se na LF iniciuje vyhodnocení práv a stromem projde na podřízený detail, který ještě není inicializován a chybí mu třeba rightsManager
        });
    }

    protected async _initializeDataSource(): Promise<void> {
        this.dataSource = new TreeViewDataSource({
            className: this.objectName,
            hierarchyAttr: this._hierarchyAttr,
            readVersioned: this.readVersioned,
            frameContext: this.frameContext,
            maxRecordCount: undefined,
        });
        this.dataSource.orderBy = this.orderBy;

        this.dataSource.attributesChanged.add(async (_senderDataSource: EditableDBDataSource, eventData: IActualObjectChangeEventArgs) => {
            if (this.isEditing)
                this.isChanged = eventData.isChanged;
            //pokud byla data právě dešifrována a týkají se AO, je potřeba přehodnotit práva.
            if (eventData.isDecrypted && this.actualObject && this.actualObject.id == (AttrValueReader.read(eventData.key))) {
                //znovu vyhodnotí práva protože, AO byl právě dešifrován
                await this.actualObject.reload();
                //await this._setRightsOnStandartAction();
                await this.actualizeRights(); // TODO-rites: #je to tu potřeba? Když je to při isActive framu voláno v předchozí metodě(this._setRightsOnStandartAction)?; c2: spíš ten předchozí řádek už nebude potřeba
                const action = this.menu.getAction(ActionType.standardDecrypt);
                if (action)
                    action.enabled = false;
                await this._triggerRightsChecking();
            }
        });

        this.ownerForm.onDoOpen.add(async (_sender, _prm) => {
            if (this.ownerForm.selectedId && this.dataGrid && this.dataGrid.dataRows.length > 0) {
                await this._selectRow(this.ownerForm.selectedId);
                this.ownerForm.selectedId = undefined;
            }
        });

        await this.updateCondition(this.dataSource);
    }

    protected override _setActualObject(actObj: OWObject, isFromMasterObjChanged: boolean = false): void {
        // je nezadouci proces zmeny objekt vyvolavat vickrat, kdyz uz tu ta stejna instance je, akorat tim vznika spousta dalsi zbytecnych ukonu
        if (this.actualObject === actObj)
            return;

        /*if (this.actualObject) {
            this.actualObject.attributeChanged.remove(this._attributeChangedEventMethod);
            this.actualObject.dataRefreshed.remove(this._objectDataRefreshedListener);
        }*/

        const previousActualObject = this.actualObject;

        // Událostmi se do hloubky informují všechny podřízené framy, protože na nich také dojde ke změně objektu
        this.setObjectInitiated.trigger(this, undefined);

        //vypnuti vstupu do editace doubleclickem
        if (actObj != null) {
            if (this.dataGrid.selectedRows.length > 0 && this.dataGrid.selectedRows[0].dataKey != actObj.id)
                return;

            super._setActualObject(actObj, isFromMasterObjChanged); // Musí být na předkovi(super), jinak by došlo k zacyklení

            /*if (this.actualObject != null) {
                this.actualObject.attributeChanged.add(this._attributeChangedEventMethod);
                this.actualObject.dataRefreshed.add(this._objectDataRefreshedListener = (sender: OWObject) => this._onObjectDataRefreshed(sender));
            }*/

            if (isFromMasterObjChanged) {
                this.dataGrid.reloadData();
            }

            this.selectionChanged.trigger(this, { sender: this, objectName: actObj.objectName, actualObject: actObj });
        } else {
            super._setActualObject(null, isFromMasterObjChanged); // Musí být na předkovi(super), jinak by došlo k zacyklení
            this.selectionChanged.trigger(this, { sender: this, objectName: "", actualObject: actObj });
        }

        this.triggerActualObjectChange(this.actualObject, previousActualObject);

        // Pokud jde o set nullu z handleru onMasterObjectChanged (tzn. jedná se o podřízený frame), jde o vymazání hodnoty před jejím (možným) nastavením na (jinou) hodnotu,
        // proto nebudu označovat načítání jako dokončené. Tak bude označeno po nastavení nové hodnoty nebo v místech, kde už je jasné,
        // že k novému nastavení nedojde (např. this._updateConditionAndRead nebo DataGrid._showData; viz reference).
        if (actObj || !isFromMasterObjChanged) // ala !(actObj == null && isFromMasterObjChanged)
            this._resolveActualObjectSetDeferred(true);
    }

    /**
     * Handler změny vybraného záznamu
     * @param event Událost o změně vybraného záznamu z DataGridu
     */
    public async onSelectionChanged(event: IDataGridSelectionChangedEvent): Promise<void> {
        if (event.detail.rowID === "-1") {
            this._setActualObject(null);
        } else {
            try {
                this.menu.showMenuLoader();

                // Načteme instanci vybraného objektu ze služby.
                const objectInstance = await this.tryToGetObject(event.detail.rowID, true);

                const selectedRow = this.dataGrid.selectedRows[0];

                // Pokud vybraný objekt nemá instanci na službě, nastavíme mu příznak,
                // že je smazaný a nastavíme null jako actualObject.
                if (!objectInstance) {
                    selectedRow.isDeleted = true;
                    this._setActualObject(null);
                    return;
                }

                // Pokud se nerovná dataKey vybraného řádku a ID instance ze serveru, skončíme.
                if (selectedRow?.dataKey !== objectInstance?.id) {
                    return;
                }

                // Nastavíme instanci ze serveru jako actualObject.
                this._setActualObject(objectInstance);
            } catch (err) {
                ErrorHandler.instance.handleError(err);
            } finally {
                this.menu.hideMenuLoader();
            }
        }
    }

    /**
    * Metoda volana pri zmene mastera
    */
    protected override async _onMasterObjectChanged(masterObjectChangedArgs?: IActualObjectChangedEventArgs): Promise<void> {
        if (!ControlUtils.isElementVisible(this.element))
            return;

        if (this._cvlMasterChangeRequest) {
            this._cvlMasterChangeRequest = false;
        }

        let lastActualObject: OWObject | undefined;
        if (masterObjectChangedArgs && masterObjectChangedArgs.actualObject == masterObjectChangedArgs.previousActualObject && this.actualObject)
            lastActualObject = this.actualObject; // pokud zůstal master objekt stejný, zapamatujeme aktuální objekt a později ho znovu označíme

        if (this.actualObject)
            this._setActualObject(null, true);

        await this._updateConditionAndRead(this.dataSource);

        if (lastActualObject) {
            await this.selectRow(lastActualObject.id);
        }

        if (this.masterFrame.actualObject == null || this.masterFrame.actualObject.isNew) {
            if (this.dataGrid)
                this.dataGrid.clearDataRows();
        }
    }

    private async _selectRow(dataKey: string): Promise<TreeViewDataRow> {
        let selectedRow: TreeViewDataRow = null;

        if (this.dataGrid)
            selectedRow = await this.dataGrid.selectRow(dataKey);
        const result: boolean = selectedRow != null;

        this._resolveActualObjectSetDeferred(result);

        return selectedRow;
    }

    public async selectRow(dataKey: string): Promise<boolean> {
        UIManager.instance.showLoader();
        try {
            await this.dataSource.waitForReload();
            const selectedRow = await this._selectRow(dataKey);
            if (selectedRow)
                selectedRow.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
            return selectedRow != null;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    public override async refreshOnClick(): Promise<void> {
        this.refreshData(true);
    }

    /**
     * Znovunačtení dat do seznamu
     * @param manualRefresh Jestli je akce vyvolána uživatelem (zabraňuje nebo povoluje označení záznamu ve framu)
     */
    public override async refreshData(manualRefresh: boolean): Promise<void> {
        this._showSaveChangesDialog(async () => this.refreshDataWithSettableReExpand(manualRefresh, true));
    }

    public async refreshDataWithSettableReExpand(manualRefresh: boolean, reExpand: boolean = true): Promise<void> {
        if (this.ownerForm.queryMode && !manualRefresh)
            return;

        const prevDataKey = (this.dataGrid.selectedRows.length > 0) ? this.dataGrid.selectedRows[0].dataKey : "";

        return this.dataGrid.reloadData().then(() => {
            if (prevDataKey != "" && reExpand !== false)
                this.selectRow(prevDataKey);
        });
    }

    /**
     * Pokusí se získat instanci objektu ze služby.
     * @param objectId ID objektu
     * @param forceLoad Zda se má vynutit znovunačtení objektu ze služby (přeskočí cache)
     */
    public async tryToGetObject(objectId: string, forceLoad = false): Promise<OWObject> | null {
        try {
            return await this.dataSource.getObject(objectId, await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), false), forceLoad);
        } catch (err) {
            ErrorHandler.instance.handleError(err);
            return null;
        }
    }

    /**
     * Zobrazí YNC dialog, pokud formulář obsahuje neuložené změny.
     * @param yesNoAction Akce, která je provedena pokud nebyl dialog zobrazen nebo je výsledek dialogu Y nebo N.
     */
    protected async _showSaveChangesDialog(yesNoAction: () => Promise<void>): Promise<void> {
        const result = await this.ownerForm.checkAndSaveForm({ forceSave: this._suppressSaveDialog });

        if (result) {
            await yesNoAction();
        }
    }

    //TODO: většinu těchto věcí by TVF potřeboval implementovat, aby k němu mohl být připojen DetailFrame a šlo přes něj zakládat, editovat a provádět další akce

    public override clearProfileInfo(): void {
        this._logger.warn("Profiles not implemented (method clearProfileInfo)!");
    }

    public override validate(_checkRecommended?: boolean): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override validateAndShowMessage(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override deleteRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override editRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override newRecord(_objectName?: string): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override saveRecords(_id?: string, _checkRecommended?: boolean): Promise<SaveResultData[]> {
        throw new Error("Method not implemented.");
    }

    public override prevRow(): void {
        throw new Error("Method not implemented.");
    }

    public override nextRow(): void {
        throw new Error("Method not implemented.");
    }

    public override createCopy(_propagateError?: boolean): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override dispose(): void {
        this.canValueLoadChanged.remove(this._canValueLoadChangedListener);
        super.dispose();
    }
}
